import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import ScheduledScanConfigurationCard from '../components/safetyReports/ScheduledScanConfigurationCard';
import type { RepeatScanSetup } from '../components/safetyReports/ScheduledScanConfigurationCard';
import ScheduledScanRunHistoryCard from '../components/safetyReports/ScheduledScanRunHistoryCard';
import { useAuth } from '../contexts/AuthContext';
import { getCapabilitiesFromUser, hasAnyCapability } from '../auth/access';
import { ensureUiScope } from '../services/api';
import { sitesApi } from '../services/sitesApi';

function readRepeatSetup(params: URLSearchParams): RepeatScanSetup | null {
  if (params.get('repeat_scan') !== '1' || params.get('handoff_version') !== '1') return null;
  const read = (key: string, max = 255) => String(params.get(key) || '').trim().slice(0, max);
  const windowSeconds = Number(params.get('window_seconds'));
  const setup: RepeatScanSetup = {
    version: 1,
    sourceWorkflowId: read('workflow_id', 512),
    siteId: read('site_id'),
    name: read('name') || 'Safety Scan',
    cameraId: read('camera_id'),
    cameraLabel: read('camera_label'),
    zoneId: read('zone_id'),
    zoneLabel: read('zone_label'),
    riskType: read('risk_type'),
    safetyCaseId: read('safety_case_id'),
    windowSeconds,
  };
  if (
    !setup.sourceWorkflowId
    || !setup.siteId
    || !Number.isSafeInteger(windowSeconds)
    || windowSeconds <= 0
    || windowSeconds > 31_536_000
    || !setup.riskType
    || (!setup.cameraId && !setup.zoneId)
  ) return null;
  return setup;
}

export default function ScheduledScanDefinitionsPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { effectiveUser } = useAuth();
  const capabilities = getCapabilitiesFromUser(effectiveUser);
  const canViewScheduledScans = hasAnyCapability(capabilities, ['scan:configure', 'scan:run']);
  const canConfigureScheduledScans = capabilities.includes('scan:configure');
  const scope = useMemo(() => ensureUiScope(), []);
  const orgId = String(scope.orgId || '').trim();
  const siteId = String(searchParams.get('site_id') || scope.siteId || '').trim();
  const repeatRequested = searchParams.get('repeat_scan') === '1';
  const parsedRepeatSetup = useMemo(() => readRepeatSetup(searchParams), [searchParams]);
  const repeatSetup = parsedRepeatSetup?.siteId === siteId ? parsedRepeatSetup : null;
  const [siteTimezone, setSiteTimezone] = useState<string>('UTC');
  const [siteLoading, setSiteLoading] = useState(Boolean(siteId));
  const [selectedDefinition, setSelectedDefinition] = useState<{
    definitionId: string;
    definitionName: string;
    scheduleId: string | null;
  }>({
    definitionId: '',
    definitionName: '',
    scheduleId: null,
  });

  useEffect(() => {
    let cancelled = false;
    if (!siteId) {
      setSiteLoading(false);
      return;
    }
    setSiteLoading(true);
    sitesApi
      .getSite(siteId)
      .then((site) => {
        if (!cancelled) setSiteTimezone(String(site.timezone || '').trim() || 'UTC');
      })
      .catch(() => {
        if (!cancelled) setSiteTimezone('UTC');
      })
      .finally(() => {
        if (!cancelled) setSiteLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [siteId]);

  if (!siteId) {
    return <Alert severity="warning">Select a site scope before configuring Scheduled Scans.</Alert>;
  }
  if (!canViewScheduledScans) {
    return <Alert severity="warning">Scheduled Scan configuration is not available for this access scope.</Alert>;
  }
  if (repeatRequested && !repeatSetup) {
    return <Alert severity="error">This Safety Scan handoff is incomplete or invalid. Return to Review and try again.</Alert>;
  }

  return (
    <Stack spacing={2.5}>
      <Box>
        <Typography variant="h4" sx={{ fontWeight: 800 }}>
          {repeatSetup ? 'Schedule this Safety Scan' : 'Manage recurring scans'}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {repeatSetup
            ? 'Confirm the exact Risk Pattern, scope, and relative observation window to repeat.'
            : 'Recurring Safety Scans are configured from a Risk Pattern Review. Existing schedules remain available here to manage.'}
        </Typography>
        {!repeatSetup ? (
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 1.5 }}>
            <Button variant="outlined" onClick={() => navigate('/incidents')}>
              Back to Review
            </Button>
            <Button variant="text" onClick={() => navigate('/acceptance/reports')}>
              Safety Reports
            </Button>
          </Stack>
        ) : null}
      </Box>
      {siteLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress aria-label="Loading site timezone" />
        </Box>
      ) : (
        <ScheduledScanConfigurationCard
          siteId={siteId}
          canConfigure={canConfigureScheduledScans}
          defaultTimezone={siteTimezone}
          repeatSetup={repeatSetup}
          onSelectionChange={setSelectedDefinition}
        />
      )}
      {!siteLoading && selectedDefinition.definitionId ? (
        <ScheduledScanRunHistoryCard
          orgId={orgId}
          siteId={siteId}
          definitionId={selectedDefinition.definitionId}
          definitionName={selectedDefinition.definitionName}
          canOperate={canViewScheduledScans}
        />
      ) : null}
    </Stack>
  );
}
